import React, { useEffect, useState } from 'react';
import SelectBox from 'devextreme-react/select-box';
import { MarsApiService as api } from '../../../../api/mars-api-service';
import NotificationEmailHtmlEditor from './NotificationEmailHtmlEditor';
import { MarsApiConfig as apiConfig } from '../../../../api/mars-api-config';
import TextBox from 'devextreme-react/text-box';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../../../common/confirm/ConfirmDialog';
import { IDialogConfig } from '../../../../types/dialog';
import { IMasterEntity } from '../../../../types/master-data';
import { NotificationEmailTemplateEnums } from '../../../../enums/notifcation-email-template-enums';
import toast from '../../../../utils/toast';

interface EmailNotificationProps {
  buId: any;
  companyId: any;
  saveConfig: boolean;
  setSaveConfig: (value: boolean) => void;
}

export default function NotificationEmailSettings(
  props: Readonly<EmailNotificationProps>
) {
  const { buId, companyId, saveConfig, setSaveConfig } = props;
  const { t } = useTranslation();

  const [emailTemplateDropDownList, setEmailTemplateDropDownList] = useState<
    any[]
  >([]);
  const [selectedEmailTemplateId, setSelectedEmailTemplateId] =
    useState<any>(null);
  const [emailBody, setEmailBody] = useState<string>('');
  const [emailSubject, setEmailSubject] = useState<string>('');
  const [showConfirmSave, setShowConfirmSave] = useState(false);
  const [entities, setEntities] = useState<IMasterEntity[]>([]);
  const [emailTemplateType, setEmailTemplateType] = useState<string>('');
  const [contentKey, setContentKey] = useState('editor-0');

  const getEmailTemplateList = async () => {
    const response = await api.get({
      url: `${apiConfig.emailTemplatesList}${companyId}`,
      params: { buid: buId },
    });
    setEmailTemplateDropDownList(response);

    if (response.length > 0) {
      setSelectedEmailTemplateId(response[0].emailTemplateId);
    }
  };

  const getEmailTemplateById = async (
    templateId: any,
    emailTemplateType: any
  ) => {
    const response = await api.get({
      url: `${apiConfig.emailTemplateGetById}${templateId}`,
    });

    setEmailSubject(response.subject);
    setEmailBody(response.templateHtml);
    setContentKey('editor-' + Date.now());
    if (emailTemplateType === NotificationEmailTemplateEnums.WelcomeEmail) {
      getEntities(true);
    } else {
      getEntities(false);
    }
  };

  const onClickOptionType = (selected: { id: any; type: string }) => {
    const selectedId = selected.id;
    const selectedType = selected.type;
    setSelectedEmailTemplateId(selectedId);
    setEmailTemplateType(selectedType);
  };

  const onValueSubmit = async () => {
    const payload = {
      id: selectedEmailTemplateId,
      subject: emailSubject,
      body: emailBody,
      buid: buId,
      companyId: companyId,
    };

    try {
      await api.put({
        url: apiConfig.emailTemplateUpdate,
        data: payload,
      });

      toast.success({
        title: t('toast.changesSavedSuccessfully'),
      });
    } catch (error) {
      toast.error({
        title: t('toast.somethingWentWrong'),
      });
    }
  };

  const getEntities = async (isWelComeMail: boolean) => {
    await api
      .get({
        url: apiConfig.masterEntities,
        params: isWelComeMail
          ? { isRequiredForWelcomeMail: true }
          : { isRequiredForRFITemplate: true },
      })
      .then((res: IMasterEntity[]) => {
        setEntities(res);
      });
  };

  const confirmSaveDialogConfig: IDialogConfig = {
    id: 'confirmSave',
    content: t('toast.confirmSaveChanges'),
    handleSubmit: () => {
      onValueSubmit();
      setShowConfirmSave(false);
      setSaveConfig(false);
    },
    handleClose: () => {
      setShowConfirmSave(false);
      setSaveConfig(false);
    },
  };

  useEffect(() => {
    getEmailTemplateList();
    getEntities(false);
  }, []);

  useEffect(() => {
    if (selectedEmailTemplateId) {
      getEmailTemplateById(selectedEmailTemplateId, emailTemplateType);
    }
  }, [selectedEmailTemplateId]);

  useEffect(() => {
    if (saveConfig) {
      setShowConfirmSave(true);
    }
  }, [saveConfig]);

  return (
    <div className="inner-wrapper pt-2">
      <div className="m-l-inner-page-header">
        <div className="m-l-form-panel-header-left-block">
          <div className="m-l-page-small-heading">
            {t('labels.notificationEmailTemplateSettings')}
          </div>
        </div>
      </div>

      <div className="row">
        <div>
          <div className="col-md-3 col-sm-6 col-lg-3 col-xs-12">
            <b>{t('labels.notificationType')}</b>
          </div>
          <br />
          <div className="col-md-3 col-sm-6 col-lg-3 col-xs-12">
            <SelectBox
              className="m-c-input-control m-c-select-box"
              placeholder="Select option type"
              dataSource={emailTemplateDropDownList}
              searchEnabled
              valueExpr="emailTemplateId"
              displayExpr="emailTemplateType"
              value={selectedEmailTemplateId}
              onValueChanged={(e: any) => {
                const selectedItem = e.component.option('selectedItem');

                const selectedId = e.value;
                const selectedType = selectedItem?.emailTemplateType;

                setSelectedEmailTemplateId(selectedId);

                onClickOptionType({ id: selectedId, type: selectedType });
              }}
            />
          </div>

          <br />
          <div className="m-c-form-group u-mb0">
            <b>{t('labels.emailTemplate')}</b>
          </div>
          <br />
          <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
            <div className="m-c-form-group mb-btm-space-22">
              <div className="m-c-mail-compose-subject-input-box">
                <span className="pe-4">{t('labels.subject')}:</span>
                <TextBox
                  className="m-c-input-control m-c-input-for-subject ps-2"
                  value={emailSubject}
                  onValueChanged={(e) => setEmailSubject(e.value)}
                />
              </div>
            </div>
          </div>

          <div>
            <NotificationEmailHtmlEditor
              content={emailBody}
              setContent={setEmailBody}
              showEditor={true}
              isDisabled={false}
              entities={entities}
              contentKey={contentKey}
            />
          </div>
        </div>
      </div>

      <ConfirmDialog
        dialogConfig={confirmSaveDialogConfig}
        isOpen={showConfirmSave}
      />
    </div>
  );
}
